import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  Calendar,
  Film,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { trackContactAction } from "@/utils/contactTracking";

interface TheatreContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TheatreContactModal: React.FC<TheatreContactModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useLanguage();
  const calendlyUrl =
    import.meta.env?.VITE_CALENDLY_URL ||
    "https://calendly.com/beatenberg-resort-spa/event-consultation";

  // Track modal open
  useEffect(() => {
    if (isOpen && typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];

      // Clear all tracking variables first (prevents data pollution)
      // NOTE: button_text and button_location are preserved to retain context of how modal was opened
      window.dataLayer.push({
        // Clear modal variables (will be reset below)
        modal_type: undefined,
        modal_name: undefined,
        parent_modal: undefined,
        page_location: undefined,
        // Preserve button variables (retain context of triggering action)
        // button_text: undefined,     // ← Keep this!
        // button_location: undefined, // ← Keep this!
        // Clear booking variables
        room_type: undefined,
        // Clear contact action variables
        contact_type: undefined,
        contact_source: undefined,
        event_category: undefined,
        event_label: undefined,
        phone_number: undefined,
        email_address: undefined,
        whatsapp_number: undefined,
        region: undefined,
        destination: undefined,
        booking_method: undefined,
        // Clear event consultation variables
        event_type: undefined,
        consultation_method: undefined,
        service_category: undefined,
        // Clear Calendly variables
        calendly_action: undefined,
        calendly_location: undefined,
      });

      // Now push the actual event with clean data
      window.dataLayer.push({
        event: "contact_theatre_modal_open",
        modal_type: "theatre",
        modal_name: "theatre_contact",
        page_location: window.location.pathname,
      });
    }
  }, [isOpen]);

  const contactOptions = [
    {
      icon: Phone,
      title: t("contactModals.theatre.phoneTitle"),
      description: t("contactModals.theatre.phoneDesc"),
      contact: "+41 33 841 00 21 | +54 9 3516 14-9349",
      action: "tel:+41338410021",
      primary: true,
      multipleNumbers: true,
      numbers: [
        { display: "+41 33 841 00 21", action: "tel:+41338410021" },
        { display: "+54 9 3516 14-9349", action: "tel:+5493516149349" },
      ],
    },
    {
      icon: Mail,
      title: t("contactModals.theatre.emailTitle"),
      description: t("contactModals.theatre.emailDesc"),
      contact: "info@beatenberg-resortspa.ch",
      action: "mailto:info@beatenberg-resortspa.ch",
      primary: false,
    },
    {
      icon: Calendar,
      title: t("contactModals.theatre.bookingTitle"),
      description: t("contactModals.theatre.bookingDesc"),
      contact: t("contactModals.theatre.bookButton"),
      action: calendlyUrl,
      primary: false,
    },
    {
      icon: MessageCircle,
      title: t("contactModals.theatre.whatsappTitle"),
      description: t("contactModals.theatre.whatsappDesc"),
      contact: "Europe: +41 76 622 01 22 | Asia: +41 79 696 86 08",
      action: "https://wa.me/41766220122",
      primary: false,
      multipleWhatsApp: true,
      whatsappNumbers: [
        {
          display: "Europe: +41 76 622 01 22",
          action: "https://wa.me/41766220122",
        },
        {
          display: "Asia: +41 79 696 86 08",
          action: "https://wa.me/41796968608",
        },
      ],
    },
  ];

  // Skip portal during SSR
  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <AnimatePresence data-oid="kws7yec">
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10000]"
            style={{ zIndex: 10000 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-oid="0zrqt3d"
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 z-[10001] flex items-center justify-center p-4"
            style={{ zIndex: 10001 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-oid="u75cqy-"
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              data-oid="ikr7tsk"
            >
              {/* Header */}
              <div
                className="relative bg-gradient-to-r from-[#A38648] to-[#A38648]/80 text-white p-6 rounded-t-2xl"
                data-oid=".ihrmpl"
              >
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
                  data-oid="fari5ca"
                >
                  <X size={20} data-oid="66mjc.r" />
                </button>

                <div className="flex items-center gap-3" data-oid="69gdt7r">
                  <div
                    className="p-3 bg-white/20 rounded-full"
                    data-oid="u:5lca:"
                  >
                    <Film size={24} data-oid="vwl24de" />
                  </div>
                  <div data-oid="i2s5p64">
                    <h2
                      className="text-2xl font-playfair font-bold"
                      data-oid="g:usels"
                    >
                      {t("contactModals.theatre.title")}
                    </h2>
                    <p className="text-white/90 font-lato" data-oid="qb13j:p">
                      {t("contactModals.theatre.subtitle")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6" data-oid="ildem-e">
                <div
                  className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6"
                  data-oid="e:4c_wu"
                >
                  {contactOptions.map((option, index) => {
                    const IconComponent = option.icon;
                    return (
                      <motion.div
                        key={option.title}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-lg h-full ${
                          option.primary
                            ? "border-[#A38648] bg-[#A38648]/5"
                            : "border-gray-200 hover:border-[#A38648]/50"
                        }`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        data-oid="x5ee.n."
                      >
                        <div
                          className="flex items-start gap-3"
                          data-oid="t8--sb1"
                        >
                          <div
                            className={`p-2 rounded-lg flex-shrink-0 ${
                              option.primary
                                ? "bg-[#A38648] text-white"
                                : "bg-gray-100 text-[#A38648]"
                            }`}
                            data-oid="olqi01u"
                          >
                            <IconComponent size={20} data-oid="9nqjned" />
                          </div>
                          <div className="flex-1" data-oid="-ianmmx">
                            <h3
                              className="font-semibold text-gray-900 mb-1"
                              data-oid="t9v5hzo"
                            >
                              {option.title}
                            </h3>
                            <p
                              className="text-sm text-gray-600 mb-2"
                              data-oid="hr:ppy1"
                            >
                              {option.description}
                            </p>
                            {(option as any).multipleWhatsApp &&
                            (option as any).whatsappNumbers ? (
                              <div className="space-y-2" data-oid="59dlbd0">
                                {(option as any).whatsappNumbers.map(
                                  (num: any, idx: number) => (
                                    <a
                                      key={idx}
                                      href={num.action}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={() =>
                                        trackContactAction(
                                          "theatre",
                                          "whatsapp",
                                          {
                                            region:
                                              idx === 0 ? "europe" : "asia",
                                            whatsapp_number:
                                              idx === 0
                                                ? "+41766220122"
                                                : "+41796968608",
                                          },
                                        )
                                      }
                                      className="contact-action-track block px-3 py-2 rounded-full text-xs font-medium bg-alpine-gold hover:bg-alpine-gold/90 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 whitespace-nowrap"
                                      data-oid="mnx3sq."
                                    >
                                      {num.display}
                                    </a>
                                  ),
                                )}
                              </div>
                            ) : (option as any).multipleNumbers &&
                              (option as any).numbers ? (
                              <div className="space-y-2" data-oid="h1hliwy">
                                {(option as any).numbers.map(
                                  (num: any, idx: number) => (
                                    <a
                                      key={idx}
                                      href={num.action}
                                      onClick={() =>
                                        trackContactAction("theatre", "phone", {
                                          region:
                                            idx === 0
                                              ? "switzerland"
                                              : "argentina",
                                          phone_number:
                                            idx === 0
                                              ? "+41338410021"
                                              : "+5493516149349",
                                        })
                                      }
                                      className={`contact-action-track block px-3 py-2 rounded-full text-xs font-medium transition-all duration-300 transform hover:scale-105 whitespace-nowrap ${
                                        option.primary
                                          ? "bg-alpine-gold hover:bg-alpine-gold/90 text-white shadow-lg hover:shadow-xl"
                                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                      }`}
                                      data-oid="nwe.5:z"
                                    >
                                      {num.display}
                                    </a>
                                  ),
                                )}
                              </div>
                            ) : option.action ? (
                              <a
                                href={option.action}
                                target={
                                  option.action.startsWith("http")
                                    ? "_blank"
                                    : undefined
                                }
                                rel={
                                  option.action.startsWith("http")
                                    ? "noopener noreferrer"
                                    : undefined
                                }
                                onClick={() => {
                                  const actionType = option.action.startsWith(
                                    "mailto:",
                                  )
                                    ? "email"
                                    : option.action.includes("calendly")
                                      ? "calendly"
                                      : "email";
                                  const details: Record<string, any> = {};
                                  if (actionType === "email")
                                    details.email_address =
                                      "info@beatenberg-resortspa.ch";
                                  if (actionType === "calendly")
                                    details.booking_method = "calendly";
                                  trackContactAction(
                                    "theatre",
                                    actionType,
                                    details,
                                  );
                                }}
                                className={`contact-action-track inline-block px-3 py-2 rounded-full text-xs font-medium transition-all duration-300 transform hover:scale-105 whitespace-nowrap ${
                                  option.primary
                                    ? "bg-alpine-gold hover:bg-alpine-gold/90 text-white shadow-lg hover:shadow-xl"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                                data-oid="ds0jl0i"
                              >
                                {option.contact}
                              </a>
                            ) : (
                              <span
                                className="inline-block px-3 py-2 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 whitespace-nowrap"
                                data-oid="tct3pmm"
                              >
                                {option.contact}
                              </span>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Theatre Services Info */}
                <div
                  className="bg-gradient-to-r from-pebble-beige to-pebble-beige/50 rounded-xl p-4 mb-6"
                  data-oid="zy96ccx"
                >
                  <h4
                    className="font-semibold text-gray-900 mb-2"
                    data-oid="jq5q4h."
                  >
                    {t("contactModals.theatre.servicesTitle")}
                  </h4>
                  <ul
                    className="text-sm text-gray-700 space-y-1"
                    data-oid="qid:yps"
                  >
                    {t("contactModals.theatre.servicesList").map(
                      (service: string, index: number) => (
                        <li key={index} data-oid="r21o653">
                          • {service}
                        </li>
                      ),
                    )}
                  </ul>
                </div>

                {/* Theatre Features */}
                <div
                  className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 mb-6"
                  data-oid="b4f4i0u"
                >
                  <h4
                    className="font-semibold text-gray-900 mb-2"
                    data-oid="slt.suv"
                  >
                    {t("contactModals.theatre.featuresTitle")}
                  </h4>
                  <div
                    className="grid grid-cols-2 gap-4 text-sm text-gray-700"
                    data-oid="0xjxo46"
                  >
                    <div data-oid="-4lhj96">
                      <strong data-oid="bvfbsn-">Capacity:</strong>{" "}
                      {t("contactModals.theatre.features.capacity")}
                    </div>
                    <div data-oid="ik.-ugm">
                      <strong data-oid="vhe4c::">Screen:</strong>{" "}
                      {t("contactModals.theatre.features.screen")}
                    </div>
                    <div data-oid="rnksev1">
                      <strong data-oid=":ouor0t">Sound:</strong>{" "}
                      {t("contactModals.theatre.features.sound")}
                    </div>
                    <div data-oid="s5a8ojx">
                      <strong data-oid="c8c262l">Seating:</strong>{" "}
                      {t("contactModals.theatre.features.seating")}
                    </div>
                    <div data-oid="281y2s7">
                      <strong data-oid="csag2wj">Lighting:</strong>{" "}
                      {t("contactModals.theatre.features.lighting")}
                    </div>
                    <div data-oid="l8n1imq">
                      <strong data-oid="e.9:1:h">Climate:</strong>{" "}
                      {t("contactModals.theatre.features.climate")}
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div
                  className="flex flex-col sm:flex-row gap-3"
                  data-oid="5snjk8p"
                >
                  <Button
                    onClick={onClose}
                    variant="outline"
                    className="flex-1"
                    data-oid="rn9wb0."
                  >
                    {t("contactModals.theatre.closeButton")}
                  </Button>
                  <a
                    href={calendlyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                    data-oid="glo67e1"
                  >
                    <Button
                      trackingType="booking"
                      trackingLabel="Book Theatre"
                      trackingLocation="theatre_modal"
                      className="w-full bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-4 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                      data-oid="1:372vi"
                    >
                      {t("contactModals.theatre.bookButton")}
                    </Button>
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default TheatreContactModal;
